/**
 * Browser-print side effect for a PDF URL.
 *
 * Servers can't print to the user's hardware, so a backend tool that produces
 * a printable document returns a PDF URL and leaves the actual printing to
 * the client. We fetch the PDF and load it into a hidden iframe as a
 * same-origin `blob:` URL so `iframe.contentWindow.print()` can open the
 * native print dialog.
 *
 * Why not set `iframe.src` directly to the S3 presigned URL? Cross-origin
 * iframes are not in the "safe subset" of Window access — per the
 * WhatWG HTML cross-origin-property-list, `print()` is NOT accessible
 * across origins, so it would fail silently (or with a SecurityError)
 * on every mainstream browser. See
 * https://html.spec.whatwg.org/multipage/nav-history-apis.html#crossoriginproperties
 *
 * `triggerBrowserPrint` powers the Print button on persisted
 * `print_document_item` timeline entries and the print action on the document
 * preview surfaces — each call prints without dedupe so the user can reprint
 * freely. The auto-fire-once-per-tool-call wrapper for `print_document`
 * stream chunks lives beside Chat's stream types, in
 * `src/components/chat/utils/handlePrintDocumentChunk.ts`.
 *
 * This file is intentionally web-only: it is never imported from mobile
 * code paths.
 */

import { logger } from '@/utils/logger/logger';

const PRINT_IFRAME_ID = 'ml-scribe-print-frame';
const PRINT_CLEANUP_MS = 60_000;

/**
 * Only `https:` sources are accepted. The iframe itself is eventually
 * loaded from a same-origin `blob:` URL constructed from the fetched
 * PDF; the raw LLM-supplied URL never reaches `iframe.src`.
 */
const ALLOWED_SOURCE_PROTOCOLS = new Set(['https:']);

/**
 * Host allowlist for the PDF source. Defaults to AWS S3 + our own
 * regional API hosts (where ml-scribe's durable
 * `build_stable_download_url` endpoint lives). Can be overridden
 * via the `NEXT_PUBLIC_PRINT_ALLOWED_HOSTS` env var —
 * comma-separated, exact or `*.suffix` patterns, e.g.
 * `"*.amazonaws.com,files.example.com"`.
 *
 * `*.api.heidihealth.com` covers every region we deploy to —
 * prod serves PHI-bearing exports from `{region}.api.heidihealth.com`
 * (e.g. `aue1`, `use1`, `euw2`, `cac1`, `euc1`), staging from
 * `registrar.`, dev from `resident.`. The wildcard is load-bearing
 * here; a hardcoded exact-subdomain list would need a code change
 * on every new-region rollout. This allowlist is a defence-in-depth
 * layer — the canonical allowlist is the CSP `connect-src` header,
 * which is configured per deploy.
 *
 * `*.aws.ext.heidi.ai` is PR-preview infrastructure only — it
 * hosts unreviewed code under per-PR subdomains and must NEVER be
 * trusted in a production build (which serves real PHI). We gate
 * it behind `NODE_ENV !== 'production'` so a prod build that forgets
 * to override `NEXT_PUBLIC_PRINT_ALLOWED_HOSTS` fails closed
 * against preview hosts rather than silently accepting fetches
 * from them. Non-prod builds keep it so local/preview dev of the
 * Print flow against `ml-scribe.<env>.<region>.aws.ext.heidi.ai`
 * keeps working without per-developer env var setup.
 */
const DEFAULT_ALLOWED_HOSTS: string[] = [
  '*.amazonaws.com',
  '*.api.heidihealth.com',
  ...(process.env.NODE_ENV !== 'production' ? ['*.aws.ext.heidi.ai'] : []),
];

const getConfiguredAllowedHosts = (): string[] => {
  const raw = process.env.NEXT_PUBLIC_PRINT_ALLOWED_HOSTS;
  if (!raw) return DEFAULT_ALLOWED_HOSTS;
  const parsed = raw
    .split(',')
    .map((h) => h.trim())
    .filter(Boolean);
  return parsed.length > 0 ? parsed : DEFAULT_ALLOWED_HOSTS;
};

const hostMatchesPattern = (host: string, pattern: string): boolean => {
  if (pattern === host) return true;
  if (pattern.startsWith('*.')) {
    const suffix = pattern.slice(1); // ".amazonaws.com"
    return host === pattern.slice(2) || host.endsWith(suffix);
  }
  return false;
};

const isSameOrigin = (parsed: URL): boolean =>
  typeof window !== 'undefined' && parsed.origin === window.location.origin;

const isAllowedPrintSource = (parsed: URL): boolean => {
  if (isSameOrigin(parsed)) return true;
  return getConfiguredAllowedHosts().some((pattern) =>
    hostMatchesPattern(parsed.host, pattern)
  );
};

const parseAllowedPrintUrl = (url: string): URL | null => {
  if (typeof window === 'undefined') return null;
  try {
    const parsed = new URL(url, window.location.href);
    // Cross-origin sources must be `https:`. For the same origin we also
    // accept `http:` so local dev and same-origin proxy/download
    // endpoints work.
    const schemeAllowed =
      ALLOWED_SOURCE_PROTOCOLS.has(parsed.protocol) ||
      (isSameOrigin(parsed) && parsed.protocol === 'http:');
    if (!schemeAllowed) return null;
    if (!isAllowedPrintSource(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
};

/**
 * Single cleanup-timer slot shared across print invocations. Each new
 * print cancels the previous one so an older timer can't remove the
 * iframe (or revoke a still-live blob URL) mid-flight during a
 * subsequent print of the same URL.
 */
let pendingCleanupTimerId: number | null = null;

/** Currently-loaded blob URL, revoked on cleanup or replacement. */
let activeBlobUrl: string | null = null;

/**
 * Monotonic request counter. Each `triggerBrowserPrint` invocation
 * snapshots the next value; if a rapid-second-click B finishes its
 * fetch before A, A must not clobber B's iframe/blob/timer when its
 * own fetch eventually resolves. We bail out of stale callbacks by
 * comparing the snapshot to this counter.
 */
let latestPrintRequestId = 0;

const isBrowser = (): boolean =>
  typeof window !== 'undefined' && typeof document !== 'undefined';

const ensureIframe = (): HTMLIFrameElement => {
  const existing = document.getElementById(PRINT_IFRAME_ID);
  if (existing instanceof HTMLIFrameElement) {
    return existing;
  }
  const iframe = document.createElement('iframe');
  iframe.id = PRINT_IFRAME_ID;
  iframe.setAttribute('aria-hidden', 'true');
  iframe.setAttribute('tabindex', '-1');
  iframe.style.position = 'fixed';
  iframe.style.left = '-9999px';
  iframe.style.top = '0';
  iframe.style.width = '100vw';
  iframe.style.height = '100vh';
  iframe.style.border = '0';
  iframe.style.visibility = 'hidden';
  iframe.style.opacity = '0';
  iframe.style.pointerEvents = 'none';
  document.body.appendChild(iframe);
  return iframe;
};

/** Open the URL in a new tab so the user can print from there. Used as
 * graceful fallback when the fetch-as-blob path fails (CORS, 403 on an
 * expired presigned URL, network error, etc.). */
const fallbackOpenInNewTab = (href: string): void => {
  if (typeof window === 'undefined') return;
  window.open(href, '_blank', 'noopener,noreferrer');
};

/**
 * Fetch a PDF URL and load it into a hidden iframe as a same-origin
 * `blob:` URL, then open the native print dialog. Used both for the
 * auto-fire on stream completion and for the explicit Print button.
 *
 * No-op outside the browser. Returns a promise that resolves once the
 * print dialog has been invoked (or a failure path has been handled);
 * callers are fire-and-forget.
 *
 * The auto-fire path dedupes by `tool_id` upstream; this function
 * intentionally does not dedupe so the button can reprint freely.
 */
export const triggerBrowserPrint = async (
  url: string,
  context?: { toolId?: string }
): Promise<void> => {
  if (!isBrowser()) return;
  if (!url) return;

  const parsed = parseAllowedPrintUrl(url);
  if (!parsed) {
    logger.error('Rejected print URL (scheme or host not allowed)', {
      toolId: context?.toolId,
    });
    return;
  }

  // Snapshot this invocation's request id BEFORE any await so we can
  // detect a newer invocation completing ahead of us.
  const requestId = ++latestPrintRequestId;
  const isStale = (): boolean => requestId !== latestPrintRequestId;

  let blobUrl: string;
  try {
    // Use `same-origin` credentials only for same-origin requests so a
    // cookie-authenticated download endpoint works; stay with `omit` for
    // cross-origin S3 so we never leak ambient credentials there.
    const response = await fetch(parsed.href, {
      credentials: isSameOrigin(parsed) ? 'same-origin' : 'omit',
    });
    if (isStale()) return;
    if (!response.ok) {
      logger.error('Failed to fetch print PDF', {
        toolId: context?.toolId,
        status: response.status,
      });
      fallbackOpenInNewTab(parsed.href);
      return;
    }
    const contentType = response.headers.get('content-type') || '';
    if (!/^application\/pdf\b/i.test(contentType)) {
      logger.error('Refused to print non-PDF response', {
        toolId: context?.toolId,
        contentType,
      });
      return;
    }
    const blob = await response.blob();
    if (isStale()) return;
    blobUrl = URL.createObjectURL(blob);
    if (isStale()) {
      // A newer call superseded us between createObjectURL and here.
      // Drop this blob instead of clobbering the newer iframe/timer.
      URL.revokeObjectURL(blobUrl);
      return;
    }
  } catch (err) {
    logger.error('Failed to fetch print PDF', {
      error: err,
      toolId: context?.toolId,
    });
    if (!isStale()) {
      fallbackOpenInNewTab(parsed.href);
    }
    return;
  }

  try {
    const iframe = ensureIframe();
    iframe.onload = () => {
      try {
        const win = iframe.contentWindow;
        if (!win) return;
        // Some browsers need the PDF viewer a tick to initialize before
        // print() works.
        win.focus();
        win.print();
      } catch (err) {
        logger.error('Failed to invoke browser print dialog', {
          error: err,
          toolId: context?.toolId,
        });
      }
    };
    iframe.src = blobUrl;

    if (pendingCleanupTimerId !== null) {
      window.clearTimeout(pendingCleanupTimerId);
    }
    if (activeBlobUrl && activeBlobUrl !== blobUrl) {
      URL.revokeObjectURL(activeBlobUrl);
    }
    activeBlobUrl = blobUrl;

    pendingCleanupTimerId = window.setTimeout(() => {
      pendingCleanupTimerId = null;
      const el = document.getElementById(PRINT_IFRAME_ID);
      if (el instanceof HTMLIFrameElement && el.src === blobUrl) {
        el.remove();
      }
      if (activeBlobUrl === blobUrl) {
        URL.revokeObjectURL(blobUrl);
        activeBlobUrl = null;
      }
    }, PRINT_CLEANUP_MS);
  } catch (err) {
    logger.error('Failed to prepare print iframe', {
      error: err,
      toolId: context?.toolId,
    });
    URL.revokeObjectURL(blobUrl);
  }
};

/** Test-only: reset the cleanup timer, blob URL, and request counter. */
export const __resetBrowserPrintForTests = (): void => {
  if (typeof window !== 'undefined' && pendingCleanupTimerId !== null) {
    window.clearTimeout(pendingCleanupTimerId);
  }
  pendingCleanupTimerId = null;
  if (activeBlobUrl) {
    URL.revokeObjectURL(activeBlobUrl);
    activeBlobUrl = null;
  }
  latestPrintRequestId = 0;
};
